import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { EntityUnionType } from 'common/types/entities/EntityInterface'
import { EntitiesStructure } from 'common/types/Page'
import { MarginType } from 'src/shared/schemas/margin.schema'
import { DiagnosticFinding, DiagnosticRule, Severity } from '../types'

const MARGIN_SIDES = [
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',
] as const satisfies ReadonlyArray<keyof MarginType>

const sideLabel: Record<(typeof MARGIN_SIDES)[number], string> = {
  marginTop: 'top',
  marginRight: 'right',
  marginBottom: 'bottom',
  marginLeft: 'left',
}

function getMarginSeverity(value: number): Severity | null {
  const abs = Math.abs(value)
  if (abs >= 500) {
    return 'high'
  }
  if (abs >= 200) {
    return 'warning'
  }
  if (value < 0) {
    return 'warning'
  }
  return null
}

function checkMargin(
  entity: EntityUnionType,
  margin: MarginType,
  device: 'desktop' | 'mobile',
  findings: DiagnosticFinding[],
): void {
  for (const side of MARGIN_SIDES) {
    const value = margin[side]
    if (value === undefined) {
      continue
    }

    const severity = getMarginSeverity(value)
    if (!severity) {
      continue
    }

    const reason = value < 0 ? 'negative' : 'unusually large'

    findings.push({
      ruleId: 'margin-issues',
      severity,
      entityId: entity.id,
      entityType: entity.type as EntityTypeEnum,
      message: `${entity.type} has ${reason} ${device} ${sideLabel[side]} margin (${value}px)`,
      metadata: { side: sideLabel[side], value, device },
    })
  }
}

export const marginRule: DiagnosticRule = {
  id: 'margin-issues',
  name: 'Margin Issues',
  run: (entities: EntitiesStructure): DiagnosticFinding[] => {
    const findings: DiagnosticFinding[] = []

    for (const entity of Object.values(entities)) {
      if ('margin' in entity && entity.margin) {
        checkMargin(entity, entity.margin, 'desktop', findings)
      }

      if ('mobileMargin' in entity && entity.mobileMargin) {
        checkMargin(entity, entity.mobileMargin, 'mobile', findings)
      }
    }

    return findings
  },
}
